/**
 * Upload-link delivery outcome (s168) — shared by the SMS path, the email path, the
 * routes that persist the attempt, and the dialog that renders it.
 *
 * The bug this exists to prevent: `sendUploadLinkSms` returned a bare `false` when Twilio
 * was unconfigured, the route passed it through as HTTP 200 `{sent:false}`, and the dialog
 * rendered its success state without reading the flag. Four sends, no texts, no error.
 *
 * A single union used as BOTH the wire `reason` and the persisted `delivery_status` keeps
 * the three layers from drifting into three vocabularies.
 */

/** Why a delivery attempt ended the way it did. Mirrors the `upload_delivery_status` enum. */
export type DeliveryReason =
  /** The provider accepted the message. */
  | "sent"
  /** This deployment has no provider credentials — our gap, not the provider's. */
  | "not_configured"
  /** The provider was called and rejected it (bad number, trial restriction, outage). */
  | "failed";

export interface DeliveryOutcome {
  sent: boolean;
  reason: DeliveryReason;
}

/** One recorded delivery attempt, as persisted onto the upload-link row. */
export interface UploadLinkDelivery {
  method: "sms" | "email";
  recipient: string;
  status: DeliveryReason;
}

export const DELIVERY_SENT: DeliveryOutcome = { sent: true, reason: "sent" };
export const DELIVERY_NOT_CONFIGURED: DeliveryOutcome = { sent: false, reason: "not_configured" };
export const DELIVERY_FAILED: DeliveryOutcome = { sent: false, reason: "failed" };

/**
 * The message shown to the planner when a send did not happen. Deliberately blames the
 * app, not the recipient — "not configured" is our problem, and a provider failure is not
 * something the planner can act on beyond using the link directly.
 */
export function deliveryFailureMessage(reason: DeliveryReason, method: "sms" | "email"): string {
  const noun = method === "sms" ? "text message" : "email";
  switch (reason) {
    case "not_configured":
      return `We couldn't send the ${noun} — ${noun} delivery isn't set up on this server. Copy the link below and use it directly.`;
    case "failed":
      return `We couldn't deliver the ${noun}. Copy the link below and use it directly, or check the address and try again.`;
    case "sent":
      return "";
  }
}
